// Core Constants Composable
// Single source of truth for all constant lookups across the application

import { ref, toRaw } from 'vue';
import type { Constant, ConstantStructure, ConstantsApiResponse } from '@/domains/constants/types';
import type { StatusVariant } from '@/domains/route/types/Route';
import { useApiQuery } from '@/composables/useApi';
import { constantsEndpoints } from '@/services/api/apiConfig';
import {
  getConstantsFromCache,
  saveConstantsToCache,
} from '@/domains/constants/repositories/constantRepository';
import { useAuthStore } from '@/domains/auth/stores/useAuthStore';

// Shared state - maps are shared across all instances of useConstants
const byCodeMaps: Record<string, Map<string, Constant>> = {};
const byIdMaps: Record<string, Map<number, Constant>> = {};
const isInitialized = ref(false);
const hasLoadedFromApi = ref(false);

export const useConstants = () => {
  // === COMPOSABLE STRUCTURE ===

  // Dependencies
  const authStore = useAuthStore();

  const {
    data: constantsData,
    error: constantsError,
    refetch: refetchConstants,
  } = useApiQuery<ConstantsApiResponse>({
    endpoint: constantsEndpoints.LIST(),
    queryParams: {
      constantNames: [
        'RouteStatusConstants',
        'ShipmentStatusConstants',
        'StatusCodeTypeConstants',
        'CustomerConstants',
        'InputTypeConstants',
        'DeviceImageTypeConstants',
        'AddressTypeConstants',
        'RouteStopTypeConstants',
      ],
    },
    queryKey: ['constants'],
    staleTime: 1000 * 60 * 60, // 1 hour TBD
    enabled: false, // Don't auto-fetch, we'll call it manually
  });

  // Infrastructure helpers
  const buildMapsFromStructure = (structure: ConstantStructure, key: string): void => {
    byCodeMaps[key] ??= new Map();
    byIdMaps[key] ??= new Map();

    const byCodeMap = byCodeMaps[key];
    const byIdMap = byIdMaps[key];

    byCodeMap.clear();
    byIdMap.clear();

    // Build byCode map
    for (const [code, constant] of Object.entries(structure.byCode)) {
      byCodeMap.set(code, constant);
    }

    // Build byId map
    for (const [idStr, constant] of Object.entries(structure.byId)) {
      const id = Number.parseInt(idStr, 10);
      if (!Number.isNaN(id)) {
        byIdMap.set(id, constant);
      }
    }
  };

  const loadConstantsFromCache = async (): Promise<boolean> => {
    const cachedData = await getConstantsFromCache();
    if (cachedData && Object.keys(cachedData).length > 0) {
      loadConstantsIntoMaps(cachedData);
      isInitialized.value = true;
      return true;
    }
    return false;
  };

  const loadConstantsIntoMaps = (constants: Record<string, ConstantStructure>): void => {
    for (const [apiKey, structure] of Object.entries(constants)) {
      buildMapsFromStructure(structure, apiKey);
    }
  };

  // === BUSINESS LOGIC ===

  // Business Actions
  const initialize = async (forceRefresh = false): Promise<void> => {
    const isAuthenticated = !!authStore.state.user;

    if (forceRefresh) {
      hasLoadedFromApi.value = false;
    }

    if (isInitialized.value && hasLoadedFromApi.value && !forceRefresh) {
      return;
    }

    if (!isAuthenticated) {
      await loadConstantsFromCache();
      return;
    }

    if (!forceRefresh) {
      await loadConstantsFromCache();
      if (hasLoadedFromApi.value) {
        return;
      }
    }

    try {
      const result = await refetchConstants({ throwOnError: false });

      if (result.error || constantsError.value) {
        const message =
          constantsError.value?.message ||
          (result.error instanceof Error ? result.error.message : null) ||
          'Failed to fetch constants';
        throw new Error(message);
      }

      const response = result.data ?? constantsData.value;
      const pascalCaseResponse =
        response && (response as { Data?: Record<string, ConstantStructure> });
      const constantsPayload = response?.data ?? pascalCaseResponse?.Data;

      if (!constantsPayload) {
        throw new Error('Constants payload missing from API response');
      }

      // Unwrap reactive proxy from TanStack Query before using
      const constants = toRaw(constantsPayload);

      loadConstantsIntoMaps(constants);

      await saveConstantsToCache(constants);

      hasLoadedFromApi.value = true;
      isInitialized.value = true;
    } catch (err) {
      console.error('Error initializing constants:', err);

      // Fallback to cache even on error
      const loadedFromCache = await loadConstantsFromCache();
      if (!loadedFromCache) {
        isInitialized.value = false;
      }
    }
  };

  // Business Selectors
  const getIdByCode = (key: string, code: string): number => {
    const map = byCodeMaps[key];
    const constant = map?.get(code);
    if (!constant) {
      throw new Error(`Constant not found by code: key="${key}", code="${code}"`);
    }
    return constant.id;
  };

  const getByCode = (key: string, code: string): Constant => {
    const map = byCodeMaps[key];
    const constant = map?.get(code);
    if (!constant) {
      throw new Error(`Constant not found by code: key="${key}", code="${code}"`);
    }
    return constant;
  };

  const getById = (key: string, id: number): Constant => {
    const map = byIdMaps[key];
    const constant = map?.get(id);
    if (!constant) {
      throw new Error(`Constant not found by id: key="${key}", id="${id}"`);
    }
    return constant;
  };

  const getRouteStatusVariant = (statusCodeId: number | undefined): StatusVariant => {
    if (!statusCodeId) {
      return 'not-ready';
    }

    const statusCodeItem = getById('RouteStatusConstants', statusCodeId);
    const code = statusCodeItem.code.toUpperCase();
    const description = statusCodeItem.description.toLowerCase();

    // Map status codes to display variants
    switch (code) {
      case 'CMP': // Completed
        return 'completed';
      case 'STD': // Started
        return 'started';
      case 'AVL': // Available
        return 'ready';
      default:
        // Fallback to description-based mapping
        if (description.includes('completed') || description.includes('finished')) {
          return 'completed';
        }
        if (description.includes('started') || description.includes('in-progress')) {
          return 'started';
        }
        if (description.includes('ready') || description.includes('assigned')) {
          return 'ready';
        }
        return 'not-ready';
    }
  };

  // === COMPOSABLE OUTPUT ===

  const actions = {
    initialize,
    // Generic lookup methods (all O(1))
    getIdByCode,
    getByCode,
    getById,
    getRouteStatusVariant,
    getRouteStatusById: (id: number) => getById('RouteStatusConstants', id),
    getRouteStatusByCode: (code: string) => getByCode('RouteStatusConstants', code),
    getAddressTypeByCode: (code: string) => getByCode('AddressTypeConstants', code),
    getAddressTypeById: (id: number) => getById('AddressTypeConstants', id),
    getRouteStopTypeByCode: (code: string) => getByCode('RouteStopTypeConstants', code),
    getRouteStopTypeById: (id: number) => getById('RouteStopTypeConstants', id),
    getShipmentStatusByCode: (code: string) => getByCode('ShipmentStatusConstants', code),
    getShipmentStatusById: (id: number) => getById('ShipmentStatusConstants', id),
    getDeviceImageTypeByCode: (code: string) => getByCode('DeviceImageTypeConstants', code),
    getDeviceImageTypeById: (id: number) => getById('DeviceImageTypeConstants', id),
    getInputTypeByCode: (code: string) => getByCode('InputTypeConstants', code),
    getInputTypeById: (id: number) => getById('InputTypeConstants', id),
    getCustomerById: (id: number) => getById('CustomerConstants', id),
  };

  return {
    actions,
  };
};
